import { useTranslation } from "react-i18next"
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { IntegrationQrCodeResult } from "@/pages/IntegrationInfo/IntegrationQrCodeResult"
import { type IntegrationQrCodeResponse } from "@/services/adminApi"

interface ProductQrCodeDialogProps {
  error: string
  isGenerating: boolean
  onOpenChange: (open: boolean) => void
  open: boolean
  productName?: string
  productImage?: string
  result: IntegrationQrCodeResponse | null

  currentIndex?: number
  totalCount?: number
  onPrevious?: () => void
  onNext?: () => void
}

export const ProductQrCodeDialog = ({
  error,
  isGenerating,
  onOpenChange,
  open,
  productName,
  productImage,
  result,
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
}: ProductQrCodeDialogProps) => {
  const { t } = useTranslation()

  const showNavigation =
    typeof currentIndex === "number" &&
    typeof totalCount === "number" &&
    totalCount > 1 &&
    (Boolean(onPrevious) || Boolean(onNext))

  const canPrev = showNavigation && !isGenerating && (currentIndex ?? 0) > 0 && Boolean(onPrevious)
  const canNext =
    showNavigation &&
    !isGenerating &&
    (currentIndex ?? 0) < (totalCount ?? 1) - 1 &&
    Boolean(onNext)
  const hasNavigation = showNavigation && !error

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-qr-dialog"
        className={cn("max-w-[798px] sm:max-w-[798px]", hasNavigation ? "min-h-[408px]" : "pb-4")}
      >
        <DialogTitle className="sr-only">
          {productName?.trim() || t("integrationInfo.qrCode.shareCatalogTitle")}
        </DialogTitle>
        <div className={cn((isGenerating || hasNavigation) && "min-h-[320px]")}>
          {isGenerating ? (
            <div
              className="flex h-[320px] items-center justify-center gap-2 text-sm text-muted-foreground"
              data-testid="product-qr-loading"
            >
              <Loader2 data-icon="inline-start" className="animate-spin" />
              {t("glasses.qrCode.generating")}
            </div>
          ) : error ? (
            <Alert variant="destructive" data-testid="product-qr-error">
              <AlertTitle>{t("integrationInfo.qrCode.errorTitle")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : result ? (
            <IntegrationQrCodeResult
              result={result}
              productName={productName}
              mediaSrc={productImage}
            />
          ) : null}
        </div>

        {hasNavigation ? (
          <div
            className="-mt-1 flex items-center justify-center gap-3 border-t pt-3 text-sm"
            data-testid="product-qr-navigation"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onPrevious?.()}
              disabled={!canPrev}
              aria-label={t("integrationInfo.qrCode.previous")}
              data-testid="product-qr-previous"
            >
              <ChevronLeft />
            </Button>
            <span className="text-sm tabular-nums text-muted-foreground">
              <span className="font-semibold text-foreground">{(currentIndex ?? 0) + 1}</span>
              <span>/{totalCount}</span>
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onNext?.()}
              disabled={!canNext}
              aria-label={t("integrationInfo.qrCode.next")}
              data-testid="product-qr-next"
            >
              <ChevronRight />
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
